import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiDatabase from 'server';
import { DEFAULT_SCOPE, DEFAULT_STATUS } from './useInvoiceFilters';

export const PAGE_SIZE = 20;
export const DISCOVERY_POLL_MS = 5000;
// A quiet read that fails is tried again; after this many failures in a row it
// is let go, and the list stays as the last good read left it.
export const MAX_QUIET_FAILURES = 5;

/**
 * The last month the api lists invoices for: the current one, counted in UTC as
 * the api does, a later month being refused with 400 PERIOD_INVALID. Counted
 * in local time, a user ahead of UTC would get the next month during the first
 * hours of the 1st. Returned as the first day of that month in local time, the
 * way the month picker and the arrows build their dates.
 */
export const getLastInvoiceMonth = (now = new Date()) =>
  new Date(now.getUTCFullYear(), now.getUTCMonth(), 1);

/** Whole months from `other` to `date`: positive when `date` is the later month. */
export const compareMonths = (date, other) =>
  (date.getFullYear() - other.getFullYear()) * 12 +
  date.getMonth() -
  other.getMonth();

// The flat list, every invoice on its own row (JBWEB-1309).
export const NO_GROUPING = 'none';

// What a list is of: its month, its filters and its grouping, not its page.
const getListKey = (period, q, scope, status, groupBy) =>
  JSON.stringify([period, q, scope, status, groupBy]);

/**
 * The invoices of a month, one page at a time, sorted and totalled by the api
 * (JBWEB-1269). While the api still looks in BeSTT for invoices it does not
 * know yet (`discovery.running`), the page is read again quietly every five
 * seconds, so the list fills in by itself.
 *
 * `filters` narrows the list on the api side: `q`, the text searched for, and
 * `scope`, where (JBWEB-1270), and `status` (JBWEB-1271). `groupBy`
 * ("company" or "group") asks for the rows of the grouped list instead
 * (JBWEB-1309): the pages are then pages of rows. The quiet reads and
 * `reload` use the filters of the moment. Only a read asked last may answer:
 * one that comes back after a newer one is dropped, whatever the filters it
 * was asked with.
 *
 * A quiet read that fails never wipes the list: it stays as last read and the
 * read is tried again. Only a visible load (first load, new month, new page,
 * new filters) ends in `failed`.
 *
 * `enabled` is false while another tab is shown: nothing is read then.
 *
 * `listKey` names the list read, its month, filters and grouping but not its
 * page: what the invoices ticked in it belong to (JBWEB-1273). `listQuery` is
 * what narrows it, without page nor grouping: a row of the grouped list reads
 * its invoices with it.
 */
const useMonthInvoices = (period, enabled, filters) => {
  const {
    q = '',
    scope = DEFAULT_SCOPE,
    status = DEFAULT_STATUS,
    groupBy = NO_GROUPING,
  } = filters ?? {};
  // The scope only says where the text is looked for: with no text it asks for
  // nothing, and changing it changes nothing.
  const askedScope = q ? scope : DEFAULT_SCOPE;
  const listKey = getListKey(period, q, askedScope, status, groupBy);
  const listQuery = useMemo(
    () => ({
      period,
      ...(q && { q, scope: askedScope }),
      ...(status && { status }),
    }),
    [period, q, askedScope, status],
  );

  // The page is kept with its list, its month and filters: a new list is page
  // 1 in the same render, so changing month or filters sends one request, not
  // two.
  const [view, setView] = useState({ listKey, page: 1 });
  if (view.listKey !== listKey) setView({ listKey, page: 1 });
  const page = view.listKey === listKey ? view.page : 1;

  // What the api answered for one list and page: its list, or a failure. An
  // answer for another list or page is never shown. `quietFailures` counts the
  // quiet reads that failed since the list was last read.
  const [outcome, setOutcome] = useState(null);
  const answer =
    outcome?.listKey === listKey && outcome.page === page ? outcome : null;
  const list = answer?.list ?? null;
  const failed = Boolean(answer?.failed);
  const isLoading = Boolean(enabled) && !list && !failed;

  const latestRead = useRef(0);

  const read = useCallback(
    (isQuiet) => {
      latestRead.current += 1;
      const readId = latestRead.current;
      if (!isQuiet) setOutcome(null);
      ApiDatabase.getMonthInvoices(
        {
          ...listQuery,
          page,
          limit: PAGE_SIZE,
          ...(groupBy !== NO_GROUPING && { groupBy }),
        },
        (data) => {
          if (readId === latestRead.current) {
            setOutcome({ listKey, page, list: data, quietFailures: 0 });
          }
        },
        (error) => {
          if (readId !== latestRead.current) return;
          if (error?.name === 'CanceledError') return;
          if (!isQuiet) {
            setOutcome({ listKey, page, failed: true });
            return;
          }
          setOutcome((current) =>
            current?.list
              ? { ...current, quietFailures: current.quietFailures + 1 }
              : current,
          );
        },
      );
    },
    [listKey, listQuery, groupBy, page],
  );

  useEffect(() => {
    if (!enabled) {
      setOutcome(null);
      return undefined;
    }
    read(false);
    return () => {
      // A read still on its way for a list, page or tab left must not answer.
      latestRead.current += 1;
    };
  }, [enabled, read]);

  // `answer` is a new object at every answer and at every failed quiet read, so
  // the next quiet read is planned again after each one: while the discovery
  // runs, and to try again after a failure, until the failures pile up.
  useEffect(() => {
    if (!enabled || !list) return undefined;
    const isRetrying = answer.quietFailures > 0;
    if (!list.discovery?.running && !isRetrying) return undefined;
    if (answer.quietFailures >= MAX_QUIET_FAILURES) return undefined;
    const timer = setTimeout(() => read(true), DISCOVERY_POLL_MS);
    return () => clearTimeout(timer);
  }, [enabled, answer, read]);

  const setPage = useCallback(
    (nextPage) => setView({ listKey, page: nextPage }),
    [listKey],
  );

  // A list on screen is refreshed in place; with none (still loading, or
  // failed) the loading state comes back.
  const reload = useCallback(() => {
    if (enabled) read(Boolean(list));
  }, [enabled, list, read]);

  return {
    listKey,
    listQuery,
    groupBy,
    list,
    isLoading,
    failed,
    page,
    setPage,
    reload,
    isFiltered: Boolean(q || status),
  };
};

export default useMonthInvoices;
